import api from 'api'
import type { AxiosRequestConfig } from 'axios'

import { validateApiResponse } from 'utils/validateApiResponse'

import {
  dataAccessRequestVerificationDetailSchema,
  getAssignableAssigneesForDataAccessRequestResponseSchema,
  getDataAccessRequestFilterAssigneesResponseSchema,
  getDataAccessRequestFilterUsersResponseSchema,
  getDataAccessRequestFilterWorkspacesResponseSchema,
  getDataAccessRequestLabelsResponseSchema,
  getDataAccessRequestParticipantFileSignedUrlResponseSchema,
  getDataAccessRequestVerificationAttachmentSignedUrlResponseSchema,
  getDataAccessRequestsVerificationResponseSchema,
  studyIdEvaluationResultSchema
} from './schemas'
import type {
  ApproveDataAccessRequestParams,
  BulkAssignDataAccessRequestsParams,
  BulkUnassignDataAccessRequestsParams,
  DataAccessRequestVerificationDetail,
  EvaluateDataAccessRequestStudyIdParams,
  GetAssignableAssigneesForDataAccessRequestResponse,
  GetDataAccessRequestFilterAssigneesResponse,
  GetDataAccessRequestFilterUsersResponse,
  GetDataAccessRequestFilterWorkspacesResponse,
  GetDataAccessRequestLabelsResponse,
  GetDataAccessRequestParticipantFileSignedUrlParams,
  GetDataAccessRequestParticipantFileSignedUrlResponse,
  GetDataAccessRequestVerificationAttachmentSignedUrlParams,
  GetDataAccessRequestVerificationAttachmentSignedUrlResponse,
  GetDataAccessRequestsVerificationParams,
  GetDataAccessRequestsVerificationResponse,
  StudyIdEvaluationResult
} from './types'

// Response shapes follow airlock-be OpenAPI (`/v1/data-access-requests/*`). Mutations return empty bodies (204).

export const getDataAccessRequestVerification = async (
  requestId: string
): Promise<DataAccessRequestVerificationDetail> => {
  const { data } = await api.get<DataAccessRequestVerificationDetail>(`/data-access-requests/verification/${requestId}`)
  return validateApiResponse(dataAccessRequestVerificationDetailSchema, data)
}

/** Empty 204 response per OpenAPI. */
export const approveDataAccessRequest = async (
  requestId: string,
  params: ApproveDataAccessRequestParams = {}
): Promise<void> => {
  await api.put(`/data-access-requests/verification/${requestId}/approve`, params)
}

export const evaluateDataAccessRequestStudyId = async (
  requestId: string,
  params: EvaluateDataAccessRequestStudyIdParams,
  options?: AxiosRequestConfig
): Promise<StudyIdEvaluationResult> => {
  const { data } = await api.post<StudyIdEvaluationResult>(
    `/data-access-requests/verification/${requestId}/study-id-evaluation`,
    params,
    options
  )

  return validateApiResponse(studyIdEvaluationResultSchema, data)
}

/** Empty 204 response per OpenAPI. */
export const rejectDataAccessRequest = async (requestId: string, reason: string): Promise<void> => {
  await api.put(`/data-access-requests/verification/${requestId}/reject`, { reason })
}

/** Empty 204 response per OpenAPI. */
export const requestAdditionalInformationOnDataAccessRequest = async (
  requestId: string,
  reason: string
): Promise<void> => {
  await api.put(`/data-access-requests/verification/${requestId}/additional-information`, { reason })
}

export const getDataAccessRequestsVerification = async (
  params: GetDataAccessRequestsVerificationParams,
  options?: AxiosRequestConfig
): Promise<GetDataAccessRequestsVerificationResponse> => {
  const { data } = await api.get<GetDataAccessRequestsVerificationResponse>('/data-access-requests/verification', {
    params,
    ...options
  })

  return validateApiResponse(getDataAccessRequestsVerificationResponseSchema, data)
}

export const getDataAccessRequestLabels = async (
  options?: AxiosRequestConfig
): Promise<GetDataAccessRequestLabelsResponse> => {
  const { data } = await api.get<GetDataAccessRequestLabelsResponse>('/data-access-requests/filters/labels', options)
  return validateApiResponse(getDataAccessRequestLabelsResponseSchema, data)
}

export const getUsersFromDataAccessRequests = async (
  options?: AxiosRequestConfig
): Promise<GetDataAccessRequestFilterUsersResponse> => {
  const { data } = await api.get<GetDataAccessRequestFilterUsersResponse>(
    '/data-access-requests/filters/users',
    options
  )
  return validateApiResponse(getDataAccessRequestFilterUsersResponseSchema, data)
}

export const getAssigneesFromDataAccessRequests = async (
  options?: AxiosRequestConfig
): Promise<GetDataAccessRequestFilterAssigneesResponse> => {
  const { data } = await api.get<GetDataAccessRequestFilterAssigneesResponse>(
    '/data-access-requests/filters/assignees',
    options
  )
  return validateApiResponse(getDataAccessRequestFilterAssigneesResponseSchema, data)
}

export const getWorkspacesFromDataAccessRequests = async (
  options?: AxiosRequestConfig
): Promise<GetDataAccessRequestFilterWorkspacesResponse> => {
  const { data } = await api.get<GetDataAccessRequestFilterWorkspacesResponse>(
    '/data-access-requests/filters/workspaces',
    options
  )
  return validateApiResponse(getDataAccessRequestFilterWorkspacesResponseSchema, data)
}

/** Empty 201 response per OpenAPI. */
export const createDataAccessRequestLabel = async (requestId: string, name: string): Promise<void> => {
  await api.post(`/data-access-requests/${requestId}/labels`, { name })
}

/** Empty 204 response per OpenAPI. */
export const deleteDataAccessRequestLabel = async (requestId: string, labelId: string): Promise<void> => {
  await api.delete(`/data-access-requests/${requestId}/labels/${labelId}`)
}

/** Empty 204 response per OpenAPI. */
export const bulkAssignDataAccessRequests = async (params: BulkAssignDataAccessRequestsParams): Promise<void> => {
  await api.put('/data-access-requests/bulk-assign', params)
}

/** Empty 204 response per OpenAPI. */
export const bulkUnassignDataAccessRequests = async (params: BulkUnassignDataAccessRequestsParams): Promise<void> => {
  await api.put('/data-access-requests/bulk-unassign', params)
}

export const getAssignableAssigneesForDataAccessRequest = async (
  requestId: string,
  options?: AxiosRequestConfig
): Promise<GetAssignableAssigneesForDataAccessRequestResponse> => {
  const { data } = await api.get<GetAssignableAssigneesForDataAccessRequestResponse>(
    `/data-access-requests/${requestId}/assignable-assignees`,
    options
  )
  return validateApiResponse(getAssignableAssigneesForDataAccessRequestResponseSchema, data)
}

export const getDataAccessRequestVerificationAttachmentSignedUrl = async ({
  requestId,
  fieldId,
  attachmentIndex,
  submission
}: GetDataAccessRequestVerificationAttachmentSignedUrlParams): Promise<GetDataAccessRequestVerificationAttachmentSignedUrlResponse> => {
  const url = `/data-access-requests/${requestId}/attachments/${fieldId}/${attachmentIndex}/signed-url`
  const { data } = submission
    ? await api.get<GetDataAccessRequestVerificationAttachmentSignedUrlResponse>(url, {
        params: { submission }
      })
    : await api.get<GetDataAccessRequestVerificationAttachmentSignedUrlResponse>(url)

  return validateApiResponse(getDataAccessRequestVerificationAttachmentSignedUrlResponseSchema, data)
}

/**
 * Get a short-lived signed URL for the participant ID file of a restricted data access request.
 * The storage location is resolved server-side, so no bucket or prefix is sent or returned.
 * Responds with 409 while the file is still being prepared or its preparation failed.
 */
export const getDataAccessRequestParticipantFileSignedUrl = async ({
  requestId
}: GetDataAccessRequestParticipantFileSignedUrlParams): Promise<GetDataAccessRequestParticipantFileSignedUrlResponse> => {
  const { data } = await api.get<GetDataAccessRequestParticipantFileSignedUrlResponse>(
    `/data-access-requests/verification/${requestId}/participant-file/signed-url`
  )

  return validateApiResponse(getDataAccessRequestParticipantFileSignedUrlResponseSchema, data)
}
